/**
 * @efacture/fiscal-core - Per-article specific tax (DGI SFE §2.17, §6.8, §6.9)
 *
 * The SFE records, for each article it applies to, a specific tax given as
 * an amount, proportional to the quantity or as a percentage of the article
 * amount (§2.17); it is displayed for each article (§6.8); the HT price is
 * understood without it and the VAT base is increased by it (§6.9).
 *
 * Two modes, one per article:
 * - AD_VALOREM: rate × net HT after discount (fraction, 0 < rate ≤ 1);
 * - FIXED_PER_UNIT: unit amount × quantity (CGI art. 339, per-unit levy).
 *
 * The four stored columns (Article, InvoiceLine, POSTransactionLine) are read
 * back through `specificTaxParamsFromFields`; every writer validates them with
 * `specificTaxFieldsError`, the single statement of the parameter rules.
 *
 * A tax group naming a special tax (L, M, N) is represented by the article's
 * FIXED_PER_UNIT forfait; `assertSpecialTaxCoverage` is that rule, and
 * `assertSpecialTaxGroupMatch` keeps such a line on the article's group.
 */
import { specialTaxRequirement } from "../constants/tax-groups.js";
export const SPECIFIC_TAX_MODES = ["AD_VALOREM", "FIXED_PER_UNIT"];
/** Maximum length of the printed tax label (§6.8, distinct mention). */
export const SPECIFIC_TAX_LABEL_MAX_LENGTH = 40;
/**
 * Decimals the stored rate column holds (Decimal(7,4)): a finer rate would
 * be rounded by the database without notice, 0.00333 to 0.0033.
 */
export const SPECIFIC_TAX_RATE_DECIMALS = 4;
/** The refusal of a rate finer than `SPECIFIC_TAX_RATE_DECIMALS`, in French. */
export const SPECIFIC_TAX_RATE_DECIMALS_REFUSAL = `Le taux de la taxe spécifique admet au plus ${SPECIFIC_TAX_RATE_DECIMALS} décimales (0,01 % près)`;
/** Label printed for a stored line that carries a mode but no label. */
export const SPECIFIC_TAX_DEFAULT_LABEL = "Taxe spécifique";
function toNumberOrNull(value) {
    if (value === null || value === undefined)
        return null;
    if (typeof value === "number")
        return value;
    if (typeof value === "string") {
        const parsed = Number(value);
        return Number.isNaN(parsed) ? null : parsed;
    }
    return value.toNumber();
}
/** True when `value` is unchanged by rounding to `decimals` places. */
function hasAtMostDecimals(value, decimals) {
    const scale = 10 ** decimals;
    return Math.round(value * scale) / scale === value;
}
function isSpecificTaxMode(value) {
    return typeof value === "string" && SPECIFIC_TAX_MODES.includes(value);
}
/**
 * Reads the stored columns back into calculator parameters.
 * Returns `null` when the row carries no mode; a stored row missing its
 * label prints under the generic label rather than failing the read path.
 */
export function specificTaxParamsFromFields(fields) {
    const mode = fields?.specificTaxMode ?? null;
    if (mode === null || !isSpecificTaxMode(mode))
        return null;
    const label = fields?.specificTaxLabel?.trim() || SPECIFIC_TAX_DEFAULT_LABEL;
    if (mode === "AD_VALOREM") {
        return { mode, rate: toNumberOrNull(fields?.specificTaxRate) ?? 0, label };
    }
    return { mode, unitAmount: toNumberOrNull(fields?.specificTaxUnitAmount) ?? 0, label };
}
/**
 * Flattens calculator parameters back into the four stored columns, the
 * shape every line writer persists.
 */
export function specificTaxFieldsFromParams(params) {
    if (params === null) {
        return {
            specificTaxMode: null,
            specificTaxRate: null,
            specificTaxUnitAmount: null,
            specificTaxLabel: null,
        };
    }
    return {
        specificTaxMode: params.mode,
        specificTaxRate: params.mode === "AD_VALOREM" ? params.rate : null,
        specificTaxUnitAmount: params.mode === "FIXED_PER_UNIT" ? params.unitAmount : null,
        specificTaxLabel: params.label,
    };
}
/**
 * The parameter rules, stated once and shared by the API validation, the
 * article service and the web form:
 * - no mode ⇒ rate, unit amount and label all absent;
 * - AD_VALOREM ⇒ rate required, 0 < rate ≤ 1, at most 4 decimals, no unit
 *   amount;
 * - FIXED_PER_UNIT ⇒ unit amount required > 0, no rate;
 * - a label of 1 to 40 characters whenever a mode is set — a caller that
 *   cannot yet default the label (an update body without the group) passes
 *   `requireLabel: false` and leaves that rule to the service.
 * Returns the French message of the first rule broken, `null` when valid.
 */
export function specificTaxFieldsError(fields, { requireLabel = true } = {}) {
    const mode = fields.specificTaxMode ?? null;
    const rate = toNumberOrNull(fields.specificTaxRate);
    const unitAmount = toNumberOrNull(fields.specificTaxUnitAmount);
    const label = fields.specificTaxLabel ?? null;
    if (mode === null) {
        if (rate !== null || unitAmount !== null || (label !== null && label.trim() !== "")) {
            return "Les paramètres de taxe spécifique nécessitent un mode (AD_VALOREM ou FIXED_PER_UNIT)";
        }
        return null;
    }
    if (!isSpecificTaxMode(mode)) {
        return "Mode de taxe spécifique invalide (AD_VALOREM, FIXED_PER_UNIT)";
    }
    const trimmedLabel = label?.trim() ?? "";
    if ((requireLabel && trimmedLabel.length === 0) ||
        trimmedLabel.length > SPECIFIC_TAX_LABEL_MAX_LENGTH) {
        return `Le libellé de la taxe spécifique est requis (1 à ${SPECIFIC_TAX_LABEL_MAX_LENGTH} caractères)`;
    }
    if (mode === "AD_VALOREM") {
        if (rate === null || !(rate > 0 && rate <= 1)) {
            return "Le taux de la taxe spécifique doit être une fraction comprise entre 0 exclu et 1 inclus";
        }
        if (!hasAtMostDecimals(rate, SPECIFIC_TAX_RATE_DECIMALS)) {
            return SPECIFIC_TAX_RATE_DECIMALS_REFUSAL;
        }
        if (unitAmount !== null) {
            return "Le montant forfaitaire n'est pas admis en mode proportionnel";
        }
        return null;
    }
    if (unitAmount === null || !(unitAmount > 0)) {
        return "Le montant forfaitaire de la taxe spécifique doit être supérieur à zéro";
    }
    if (rate !== null) {
        return "Le taux n'est pas admis en mode forfaitaire par unité";
    }
    return null;
}
/**
 * French refusal when `taxGroup` names a special tax (L, M, N) and the
 * article's specific tax is not a FIXED_PER_UNIT forfait above zero; `null`
 * when the group is ordinary or the forfait represents the levy. An ordinary
 * group carrying a forfait is never refused here.
 */
export function assertSpecialTaxCoverage(taxGroup, specificTax, articleName) {
    const requirement = specialTaxRequirement(taxGroup);
    if (requirement === null)
        return null;
    if (specificTax?.mode === "FIXED_PER_UNIT" && specificTax.unitAmount > 0)
        return null;
    const article = articleName?.trim() ? `l'article « ${articleName.trim()} »` : "l'article";
    return `Le groupe ${taxGroup} (${requirement.label}) exige un forfait par unité sur ${article} : renseignez la taxe spécifique en mode « Forfait par unité »`;
}
/**
 * French refusal when the line's group differs from the group of the row its
 * tax parameters come from (the article, or a credit note's original line)
 * and either names a special tax (L, M, N): the levy is declared under the
 * article's group, so a line cannot move it or book an ordinary forfait as
 * the levy. `null` when the groups agree, neither is special, or unknown.
 */
export function assertSpecialTaxGroupMatch(lineTaxGroup, articleTaxGroup, articleName) {
    if (!articleTaxGroup || articleTaxGroup === lineTaxGroup)
        return null;
    const lineRequirement = specialTaxRequirement(lineTaxGroup);
    const articleRequirement = specialTaxRequirement(articleTaxGroup);
    if (lineRequirement === null && articleRequirement === null)
        return null;
    const named = (code, requirement) => requirement ? `${code} (${requirement.label})` : code;
    const article = articleName?.trim() ? `l'article « ${articleName.trim()} »` : "l'article";
    return `Le groupe ${named(lineTaxGroup, lineRequirement)} de la ligne diffère du groupe ${named(articleTaxGroup, articleRequirement)} de ${article} : une taxe spéciale se facture sous le groupe de l'article`;
}
/**
 * The fields with the label filled from the group's special tax when a mode
 * is set and the label is blank; a label the tenant set is kept unchanged.
 */
export function withSpecialTaxLabelDefault(taxGroup, fields) {
    const requirement = taxGroup ? specialTaxRequirement(taxGroup) : null;
    if (requirement === null || !fields.specificTaxMode)
        return fields;
    if ((fields.specificTaxLabel ?? "").trim() !== "")
        return fields;
    return { ...fields, specificTaxLabel: requirement.label };
}
//# sourceMappingURL=specific-tax.js.map